import { API_BASE } from './base'
import type {
  CreatePortfolioRequest,
  CreatedPortfolio,
  ManagerPortfolio,
  PortfolioDetail,
  PortfolioNavPoint,
  PortfolioPosition,
  PortfolioConfigSnapshot,
  RebalanceItem,
  SignalItem,
} from '@/types/portfolio'

async function request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { detail?: string }).detail ?? `HTTP ${res.status}`)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return res.json() as Promise<T>
}

export const getPortfolios = (): Promise<ManagerPortfolio[]> =>
  request(`${API_BASE}/portfolios`)

export const getPortfolioDetail = (id: number): Promise<PortfolioDetail> =>
  request(`${API_BASE}/portfolios/${id}`)

export const createPortfolio = (data: CreatePortfolioRequest): Promise<CreatedPortfolio> =>
  request(`${API_BASE}/portfolios`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

export const updatePortfolioStatus = (id: number, status: number): Promise<PortfolioDetail> =>
  request(`${API_BASE}/portfolios/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })

export const deletePortfolio = (id: number): Promise<void> =>
  request(`${API_BASE}/portfolios/${id}`, { method: 'DELETE' })

export const getPortfolioNav = (
  id: number,
  limit = 365,
): Promise<{ items: PortfolioNavPoint[]; total: number }> =>
  request(`${API_BASE}/portfolios/${id}/nav?limit=${limit}`)

export const getPortfolioPositions = (
  id: number,
): Promise<{ items: PortfolioPosition[]; total: number }> =>
  request(`${API_BASE}/portfolios/${id}/positions`)

export const getPortfolioRebalances = (
  id: number,
  limit = 10,
  offset = 0,
): Promise<{ items: RebalanceItem[]; total: number; limit: number; offset: number }> =>
  request(`${API_BASE}/portfolios/${id}/rebalances?limit=${limit}&offset=${offset}`)

export const getPortfolioSignals = (
  id: number,
  limit = 50,
  offset = 0,
  full = true,
): Promise<{ items: SignalItem[]; total: number; limit: number; offset: number }> =>
  request(`${API_BASE}/portfolios/${id}/signals?limit=${limit}&offset=${offset}&full=${full}`)

export const getPortfolioSignalDetail = (
  portfolioId: number,
  signalId: number,
): Promise<{ portfolio_id: number; item: SignalItem }> =>
  request(`${API_BASE}/portfolios/${portfolioId}/signals/${signalId}`)

export const triggerRebalance = (id: number): Promise<{ status: string; message: string }> =>
  request(`${API_BASE}/portfolios/${id}/rebalance`, { method: 'POST' })

export const liquidatePortfolio = (id: number): Promise<{ id: number; status: number }> =>
  request(`${API_BASE}/portfolios/${id}/liquidate`, { method: 'POST' })

export const triggerRiskCheck = (id: number): Promise<{ status: string; message: string }> =>
  request(`${API_BASE}/portfolios/${id}/risk-check`, { method: 'POST' })

export const getPortfolioConfigSnapshot = (id: number): Promise<PortfolioConfigSnapshot> =>
  request(`${API_BASE}/portfolios/${id}/config-snapshot`)
